import type {
  CarePlixAddScanResponse,
  CarePlixTokenResponse,
  ScanFinishPayload,
} from '@/types/careplix.types';
import type { PostureValue } from '@/types/user.types';
import { POSTURE_API_MAP } from '@/utils/constants';

const API_BASE =
  import.meta.env.VITE_CAREPLIX_API_BASE_URL ??
  import.meta.env.VITE_API_BASE_URL ??
  'https://sdk-staging.carenow.healthcare';

function isSuccessStatus(code: number | undefined): boolean {
  return code !== undefined && String(code).startsWith('2');
}

async function careplixPost<T>(endpoint: string, body: unknown, auth = false): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth) {
    const secret = import.meta.env.VITE_CAREPLIX_SECRET_KEY;
    if (secret) headers.Authorization = secret;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  const data = (await response.json()) as T & { statusCode?: number; message?: string };
  if (!response.ok || (data.statusCode !== undefined && !isSuccessStatus(data.statusCode))) {
    throw new Error(data.message ?? `Sugar Free API error (${response.status})`);
  }
  return data;
}

export interface AddScanInput {
  apiKey: string;
  scanToken: string;
  posture: PostureValue;
  dob: string;
  gender: 'male' | 'female';
  heightCm: number;
  weightKg: number;
  scanData: ScanFinishPayload;
}

export async function createScanToken(
  apiKey: string,
  employeeId: string,
): Promise<{ token: string; expiresAt: number }> {
  const data = await careplixPost<CarePlixTokenResponse>(
    '/vitals/create-token',
    { api_key: apiKey, employee_id: employeeId },
    true,
  );

  return {
    token: data.scan_token,
    expiresAt: new Date(data.token_expiry.replace(' ', 'T')).getTime(),
  };
}

export async function submitScan(input: AddScanInput): Promise<CarePlixAddScanResponse> {
  const isApple = /iPhone|iPad|iPod|Macintosh|Mac/i.test(navigator.userAgent);
  const device = `RPPG_CAREPLIX_FACE_${isApple ? 'IOS' : 'ANDROID'}`;

  return careplixPost<CarePlixAddScanResponse>('/vitals/add-scan', {
    api_key: input.apiKey,
    scan_token: input.scanToken,
    posture: POSTURE_API_MAP[input.posture],
    dob: input.dob,
    gender: input.gender === 'male' ? 'Male' : 'Female',
    metadata: {
      physiological_scores: {
        height: String(input.heightCm.toFixed(1)),
        weight: String(input.weightKg),
      },
      ppg_time: input.scanData.ppg_time,
      raw_intensity: input.scanData.raw_intensity,
      device,
      fps: input.scanData.average_fps,
    },
  });
}

export async function updateScanStatus(
  apiKey: string,
  employeeId: string,
  scanToken: string,
  fireType: 'on_error' | 'on_success' | 'on_halt',
  fireReason: string,
  errorList: Array<{ duration?: number; code?: string; stackTrace?: string }> = [],
): Promise<void> {
  try {
    await careplixPost('/vitals/update-scan-status', {
      api_key: apiKey,
      employee_id: employeeId,
      scan_token: scanToken,
      fire_type: fireType,
      fire_reason: fireReason,
      error_list: errorList,
    });
  } catch {
    // Non-blocking telemetry
  }
}
